const TRANSITION_MS = 300; // matches CSS transition duration
const SCROLL_CLOSE_THRESHOLD = 12; // px — ignores trackpad jitter, closes on intentional scroll

const initMegaMenu = () => {
  const OPEN_CLASS = 'bfx-is-open';
  const MEGA_ITEM_SELECTOR = '.site-nav--is-megamenu .site-nav__details';
  let current = null;
  let inFlight = null; // tracks an in-progress close { dropdown, timer }
  let currentTier2 = null;
  let scrollStartY = 0;

  const getDropdown = (details) => details.querySelector('.site-nav__dropdown.megamenu');

  const setAriaExpanded = (details, expanded) => {
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', String(expanded));
  };

  // Single source of truth for trigger aria-expanded: true only for the open panel.
  // Impulse's base menuDetailsHandler toggles aria-expanded on every document click and gets
  // the closed state wrong — it sets aria-expanded="true" on a *collapsed* <details>. Its
  // listeners and ours fire in an unspecified order, so we can't rely on a synchronous re-sync;
  // re-assert on the next frame, after every sync click handler has run (E20-1400, WCAG 4.1.2).
  const normalizeTriggerAria = () => {
    document
      .querySelectorAll(MEGA_ITEM_SELECTOR)
      .forEach((details) => setAriaExpanded(details, details === current));
  };

  // ── Focus trap ───────────────────────────────────────────────────────────────
  // Trap within the <details> (summary + panel) so Tab cycles the panel's controls
  // and wraps back to the triggering summary instead of escaping to the page behind.
  // ESC / close / outside-click are the documented ways out (no keyboard trap).
  let trapController = null;

  const getFocusable = (container) =>
    // `summary` first so the triggering <summary> is the first stop — Tab off the last control
    // wraps back to it, and Shift+Tab off it wraps to the last control (no escape to the page).
    [
      ...container.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((el) => el.offsetParent !== null); // skips hidden sublinks / inactive center panels

  const releaseTrap = () => {
    trapController?.abort();
    trapController = null;
  };

  const trapFocus = (details) => {
    releaseTrap();
    trapController = new AbortController();
    details.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Tab') return;
        const focusable = getFocusable(details);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      },
      { signal: trapController.signal }
    );
  };

  const abortClose = () => {
    if (!inFlight) return;
    clearTimeout(inFlight.timer);
    inFlight = null;
  };

  // Instantly close without animation — used for category switches
  const snapClosed = (details) => {
    const dropdown = getDropdown(details);
    if (!dropdown) return;
    releaseTrap();
    clearActiveTier2();
    document.body.classList.remove('bfx-mega-menu-is-open');
    dropdown.style.transition = 'none';
    dropdown.classList.remove(OPEN_CLASS);
    details.removeAttribute('open');
    setAriaExpanded(details, false);
    requestAnimationFrame(() => {
      dropdown.style.transition = '';
    });
  };

  const open = (details) => {
    const closingDropdown = inFlight?.dropdown;
    abortClose();
    clearActiveTier2();

    const dropdown = getDropdown(details);
    if (!dropdown) return;

    // If aborting a mid-close on the same panel, snap to 0fr for a consistent animation
    if (closingDropdown === dropdown) {
      dropdown.style.transition = 'none';
      dropdown.getBoundingClientRect();
      dropdown.style.transition = '';
    }

    // When stuck, .site-header has a CSS transform which makes it a containing block for
    // our position:fixed panel — so `top` is measured in .site-header's local space, not the
    // viewport. Use offsetHeight (local height) so the transform's announcement-bar offset isn't
    // double-counted. When not stuck, no transform exists so we need viewport coords instead.
    const siteHeader = document.querySelector('.site-header');
    if (siteHeader) {
      const isStuck = siteHeader.classList.contains('site-header--stuck');
      const bottom = isStuck ? siteHeader.offsetHeight : siteHeader.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--bfx-header-bottom', `${bottom}px`);
    }

    document.body.classList.add('bfx-mega-menu-is-open');
    details.setAttribute('open', '');
    setAriaExpanded(details, true);
    dropdown.getBoundingClientRect(); // force reflow so transition plays from 0fr
    dropdown.classList.add(OPEN_CLASS);
    autoSelectFirstTier2(details);
    trapFocus(details);
    scrollStartY = window.scrollY;
  };

  const close = (details, callback) => {
    abortClose();
    releaseTrap();
    clearActiveTier2();
    const dropdown = getDropdown(details);
    if (!dropdown) return;

    // Re-assert [open] so Impulse's menuDetailsHandler can't remove it mid-animation
    // and trigger UA display:none before the clip-path transition completes.
    details.setAttribute('open', '');
    document.body.classList.remove('bfx-mega-menu-is-open');
    setAriaExpanded(details, false);
    dropdown.classList.remove(OPEN_CLASS);

    // Defer removeAttribute('open') until after the clip-path animation completes.
    // Removing it synchronously triggers the UA stylesheet display:none which interrupts the transition.
    const timer = setTimeout(() => {
      inFlight = null;
      details.removeAttribute('open');
      if (callback) callback();
    }, TRANSITION_MS);

    inFlight = { dropdown, timer };
  };

  // ── Tier 2 state ─────────────────────────────────────────────────────────────

  const setActiveTier2 = (item) => {
    if (currentTier2 === item) return;
    if (currentTier2) {
      currentTier2.classList.remove('bfx-is-active');
      const prevSublinks = currentTier2.querySelector('.bfx-mega-menu__sublinks');
      if (prevSublinks) {
        currentTier2
          .querySelector('.bfx-mega-menu__tier2-link')
          ?.setAttribute('aria-expanded', 'false');
        prevSublinks.setAttribute('hidden', '');
      }
    }
    currentTier2 = item;
    if (item) {
      item.classList.add('bfx-is-active');
      const btn = item.querySelector('.bfx-mega-menu__tier2-link');
      const sublinks = item.querySelector('.bfx-mega-menu__sublinks');
      if (sublinks) {
        btn?.setAttribute('aria-expanded', 'true');
        sublinks.removeAttribute('hidden');
      }
      document.dispatchEvent(
        new CustomEvent('megaMenu:subcategoryChange', {
          detail: {
            parentCategory: btn?.dataset.parentCategory,
            subcategory: btn?.dataset.subcategoryHandle,
            subcategoryTitle: btn?.dataset.subcategoryTitle,
            subcategoryUrl: btn?.dataset.subcategoryUrl,
          },
        })
      );
    }
  };

  const clearActiveTier2 = () => setActiveTier2(null);

  const autoSelectFirstTier2 = (details) => {
    const firstItem = details.querySelector('.bfx-mega-menu__tier2-item');
    if (firstItem) setActiveTier2(firstItem);
  };

  // ── Tier 1 + Tier 2 state ────────────────────────────────────────────────────

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) {
      const closing = current;
      const trigger = closing.querySelector('summary');
      current = null;
      close(closing, () => {
        trigger?.focus();
      });
    }
  });

  // Delegated click handler covers: tier-1 triggers, close button, outside-click close.
  // Delegating (vs direct summary listeners) survives theme editor section re-renders.
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;

    // Tier-2 link — a <button> when it has sublinks, a plain <a> when it doesn't
    const tier2Link = e.target.closest(`${MEGA_ITEM_SELECTOR} .bfx-mega-menu__tier2-link`);
    if (tier2Link) {
      const item = tier2Link.closest('.bfx-mega-menu__tier2-item');
      const hasSublinks = !!item.querySelector('.bfx-mega-menu__sublinks');
      if (!hasSublinks) return; // it's an <a> — let the browser navigate natively
      if (currentTier2 === item) {
        window.location.href = tier2Link.dataset.subcategoryUrl; // re-activating the open one navigates
        return;
      }
      setActiveTier2(item);
      return;
    }

    // Tier-1 trigger
    const summary = e.target.closest(`${MEGA_ITEM_SELECTOR} > summary`);
    if (summary) {
      e.preventDefault();
      const details = summary.closest('.site-nav__details');

      if (!current) {
        current = details;
        open(details);
      } else if (current === details) {
        const closing = current;
        current = null;
        close(closing, () => {
          summary.focus();
        });
      } else {
        // Category switch: snap old panel closed, open new one immediately
        const prev = current;
        abortClose();
        snapClosed(prev);
        current = details;
        open(details);
      }
      return;
    }

    // Close button
    if (e.target.closest('[data-mega-menu-close]')) {
      if (current) {
        const closing = current;
        const trigger = closing.querySelector('summary');
        current = null;
        close(closing, () => {
          trigger?.focus();
        });
      }
      return;
    }

    // Outside click closes the open panel
    if (current && !current.contains(e.target)) {
      const closing = current;
      current = null;
      close(closing);
    }
  });

  // aria-expanded backstop: Impulse's menuDetailsHandler flips a collapsed trigger to
  // aria-expanded="true" on any document click. Re-assert the correct state on the next frame —
  // after all synchronous handlers — so it wins regardless of listener order (E20-1400).
  document.addEventListener('click', () => requestAnimationFrame(normalizeTriggerAria));

  // Close when any drawer opens. The panel and .drawer both sit at z-index 30 and the panel is
  // later in the DOM, so it paints over the drawer and hides its contents (E20-1380). The
  // outside-click handler above can't catch this: Impulse binds its opener directly to the
  // button and calls stopPropagation() (theme.js Drawers.open), so the click never reaches
  // document. Impulse's own drawerOpen event is the reliable signal, and it covers every
  // theme.Drawers instance — cart, store availability, collection filters, mobile nav.
  // Snap rather than animate: the drawer is already sliding in, and snapClosed releases our
  // focus trap before the drawer installs its own.
  document.addEventListener('drawerOpen', () => {
    if (!current) return;
    const closing = current;
    current = null;
    snapClosed(closing);
  });

  // Close on meaningful page scroll — snap (no animation) since header height shifts mid-animation look wrong
  // Internal overflow scroll (product list) doesn't bubble to window so no contains check needed
  window.addEventListener(
    'scroll',
    () => {
      if (!current) return;
      if (Math.abs(window.scrollY - scrollStartY) > SCROLL_CLOSE_THRESHOLD) {
        const closing = current;
        current = null;
        snapClosed(closing);
      }
    },
    { passive: true }
  );

  // Close on resize — header height changes on resize which causes the same gap/jump issue
  window.addEventListener(
    'resize',
    () => {
      if (!current) return;
      const closing = current;
      current = null;
      snapClosed(closing);
    },
    { passive: true }
  );

  // Dev-mode: transfer [open] set by Liquid to bfx-is-open without animation
  document.querySelectorAll(`${MEGA_ITEM_SELECTOR}[open]`).forEach((details) => {
    const dropdown = getDropdown(details);
    if (dropdown) {
      dropdown.style.transition = 'none';
      dropdown.classList.add(OPEN_CLASS);
      details.removeAttribute('open');
      setAriaExpanded(details, true);
      document.body.classList.add('bfx-mega-menu-is-open');
      current = details;
      autoSelectFirstTier2(details);
      requestAnimationFrame(() => {
        dropdown.style.transition = '';
      });
    }
  });
};

initMegaMenu();
